import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiMessageCircle, FiX, FiZoomIn, FiChevronLeft, FiChevronRight, FiShare2, FiCheck } from 'react-icons/fi';
import { FaTelegramPlane, FaFacebook, FaTwitter, FaWhatsapp, FaLink } from 'react-icons/fa';
import api from '../services/api';
import { API_ENDPOINTS, getImageUrl } from '../utils/constants';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import ProductCard from '../components/ProductCard';
import { toast } from 'react-hot-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [latestProducts, setLatestProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Your Telegram username
  const TELEGRAM_USERNAME = 'Bossasean168';
  
  // Your website URL
  const WEBSITE_URL = 'https://asean168phone.com';

  // Function to update meta tags for product
  const updateMetaTags = (product) => {
    if (!product) return;

    const productTitle = `${product.title} | ASEAN Phone Shop`;
    const productDescription = `ទិញ ${product.title} តម្លៃ $${product.price.toFixed(2)}${product.discount_percent ? ` បញ្ចុះ ${product.discount_percent}%` : ''} ពី ASEAN Phone Shop - ហាងលក់ទូរស័ព្ទដៃថ្មី និងគ្រឿងបន្លាស់ដើម`;
    const productImage = product.images?.[0] 
      ? getImageUrl(product.images[0].filepath, 'products')
      : `${WEBSITE_URL}/logo3.png`;
    const productUrl = `${WEBSITE_URL}/products/${id}`;

    // Update page title
    document.title = productTitle;

    // Update or create meta tags
    const updateOrCreateMeta = (name, content, isProperty = false) => {
      let selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (meta) {
        meta.setAttribute(isProperty ? 'property' : 'name', name);
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    // Update standard meta tags
    updateOrCreateMeta('description', productDescription, false);
    updateOrCreateMeta('keywords', `${product.title}, ទូរស័ព្ទដៃ, លក់ទូរស័ព្ទ, iPhone, Samsung, Xiaomi, Asian Phone`, false);
    
    // Update Open Graph tags
    updateOrCreateMeta('og:title', productTitle, true);
    updateOrCreateMeta('og:description', productDescription, true);
    updateOrCreateMeta('og:image', productImage, true);
    updateOrCreateMeta('og:url', productUrl, true);
    updateOrCreateMeta('og:type', 'product', true);
    
    // Update Twitter Card tags
    updateOrCreateMeta('twitter:title', productTitle, false);
    updateOrCreateMeta('twitter:description', productDescription, false);
    updateOrCreateMeta('twitter:image', productImage, false);
    updateOrCreateMeta('twitter:url', productUrl, false);
    updateOrCreateMeta('twitter:card', 'summary_large_image', false);
    
    // Product specific meta tags
    updateOrCreateMeta('product:price:amount', product.price.toFixed(2), true);
    updateOrCreateMeta('product:price:currency', 'USD', true);
    updateOrCreateMeta('product:availability', 'in stock', true);
  };

  // Reset meta tags to default when leaving product page
  const resetMetaTags = () => {
    // Reset to default values
    document.title = 'ASEAN 168 Phone Shop | អាស៊ាន168ហ្វូនសប';
    
    const defaultDescription = 'Asian Phone - ហាងលក់ទូរស័ព្ទដៃថ្មី និងគ្រឿងបន្លាស់ដើម តម្លៃសមរម្យ ធានាគុណភាព ១០០%។ សេវាកម្មជួសជុលទូរស័ព្ទរហ័ស និងមានជំនាញ។';
    const defaultImage = `${WEBSITE_URL}/logo3.png`;
    const defaultUrl = WEBSITE_URL;
    
    const updateOrCreateMeta = (name, content, isProperty = false) => {
      let selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute('content', content);
      }
    };
    
    updateOrCreateMeta('description', defaultDescription, false);
    updateOrCreateMeta('og:title', 'Asean168 Phone | អាស៊ាន168ហ្វូន -លក់ទូរស័ព្ទថ្មី តម្លៃពិសេស', true);
    updateOrCreateMeta('og:description', defaultDescription, true);
    updateOrCreateMeta('og:image', defaultImage, true);
    updateOrCreateMeta('og:url', defaultUrl, true);
    updateOrCreateMeta('twitter:title', 'Asean168 Phone | អាស៊ាន168ហ្វូន -លក់ទូរស័ព្ទថ្មី តម្លៃពិសេស', false);
    updateOrCreateMeta('twitter:description', defaultDescription, false);
    updateOrCreateMeta('twitter:image', defaultImage, false);
    updateOrCreateMeta('twitter:url', defaultUrl, false);
  };

  useEffect(() => {
    fetchProduct();
    fetchLatestProducts();
    
    // Cleanup function to reset meta tags when component unmounts
    return () => {
      resetMetaTags();
    };
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.PRODUCT_DETAIL(id));
      setProduct(response.data);
      
      // Update meta tags with product data
      if (response.data) {
        updateMetaTags(response.data);
      }
    } catch (err) {
      setError('មិនអាចផ្ទុកព័ត៌មានទំនិញបានទេ');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestProducts = async () => {
    try {
      setLoadingLatest(true);
      const response = await api.get(API_ENDPOINTS.PRODUCTS, {
        params: { 
          limit: 5, 
          sort: 'latest',
          exclude: id
        }
      });
      setLatestProducts(response.data);
    } catch (err) {
      console.error('Error fetching latest products:', err);
    } finally {
      setLoadingLatest(false);
    }
  };

  // Get product image URL for sharing
  const getProductShareImage = () => {
    if (product?.images && product.images.length > 0) {
      return getImageUrl(product.images[0].filepath, 'products');
    }
    return `${WEBSITE_URL}/logo3.png`;
  };

  // Get share data
  const getShareData = () => {
    const shareTitle = product?.title || 'ASEAN Phone Shop';
    const shareDescription = ``;
    const shareUrl = `${WEBSITE_URL}/products/${id}`;
    const shareImage = getProductShareImage();
    
    return { shareTitle, shareDescription, shareUrl, shareImage };
  };

  // Share using Web Share API (mobile)
  const handleNativeShare = async () => {
    const { shareTitle, shareDescription, shareUrl } = getShareData();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
        toast.success('ចែករំលែកដោយជោគជ័យ');
      } catch (err) {
        console.error('Share error:', err);
        copyLinkToClipboard();
      }
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  // Copy link to clipboard
  const copyLinkToClipboard = async () => {
    try {
      const { shareUrl } = getShareData();
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('តំណត្រូវបានចម្លង');
      setTimeout(() => setCopied(false), 3000);
      setShowShareMenu(false);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('មិនអាចចម្លងតំណបានទេ');
    }
  };

  // Share to Facebook
  const shareToFacebook = () => {
    const { shareUrl, shareTitle, shareDescription } = getShareData();
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle + ' - ' + shareDescription)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    toast.success('បើក Facebook');
  };

  // Share to Telegram
  const shareToTelegram = () => {
    const { shareUrl, shareTitle, shareDescription, shareImage } = getShareData();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle + '\n' + shareDescription + '\n' + shareImage)}`;
    window.open(telegramUrl, '_blank');
    setShowShareMenu(false);
    toast.success('បើក Telegram');
  };

  // Share to Twitter
  const shareToTwitter = () => {
    const { shareUrl, shareTitle } = getShareData();
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    toast.success('បើក Twitter');
  };

  // Share to WhatsApp
  const shareToWhatsApp = () => {
    const { shareUrl, shareTitle, shareDescription, shareImage } = getShareData();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareTitle + '\n' + shareDescription + '\n' + shareUrl + '\n' + shareImage)}`;
    window.open(whatsappUrl, '_blank');
    setShowShareMenu(false);
    toast.success('បើក WhatsApp');
  };

  const handleTelegramContact = () => {
    const productLink = `${WEBSITE_URL}/products/${id}`;
    
    const message = encodeURIComponent(
      `សួស្តី! ខ្ញុំចាប់អារម្មណ៍លើផលិតផលនេះ៖\n\n` +
      `📱 *ឈ្មោះផលិតផល:* ${product?.title}\n` +
      `💰 *តម្លៃ:* $${product?.price.toFixed(2)}\n` +
      `🏷️ *បញ្ចុះតម្លៃ:* ${product?.discount_percent}%\n` +
      `💵 *តម្លៃថ្មី:* $${(product?.price * (1 - product?.discount_percent / 100)).toFixed(2)}\n` +
      `📂 *ប្រភេទ:* ${product?.category?.name || 'គ្មាន'}\n` +
      `🔗 *តំណផលិតផល:* ${productLink}\n\n` +
      `សូមផ្តល់ព័ត៌មានបន្ថែមផង។ អរគុណ!`
    );

    window.open(`https://t.me/${TELEGRAM_USERNAME}?text=${message}`, '_blank');
    toast.success('បើក Telegram ដើម្បីទំនាក់ទំនង');
  };

  const handlePhoneContact = () => {
    window.location.href = 'tel:+85510768240';
    toast.success('កំពុងភ្ជាប់ទូរស័ព្ទ...');
  };

  const openModal = (imageUrl) => {
    setModalImage(imageUrl);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPreviousImage = () => {
    const images = product?.images || [];
    const newIndex = (selectedImage - 1 + images.length) % images.length;
    setSelectedImage(newIndex);
    setModalImage(images[newIndex] 
      ? getImageUrl(images[newIndex].filepath, 'products')
      : 'https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image');
  };

  const goToNextImage = () => {
    const images = product?.images || [];
    const newIndex = (selectedImage + 1) % images.length;
    setSelectedImage(newIndex);
    setModalImage(images[newIndex] 
      ? getImageUrl(images[newIndex].filepath, 'products')
      : 'https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image');
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Handle arrow keys for navigation
  useEffect(() => {
    const handleArrowKeys = (e) => {
      if (isModalOpen) {
        if (e.key === 'ArrowLeft') {
          goToPreviousImage();
        } else if (e.key === 'ArrowRight') {
          goToNextImage();
        }
      }
    };
    window.addEventListener('keydown', handleArrowKeys);
    return () => window.removeEventListener('keydown', handleArrowKeys);
  }, [isModalOpen, selectedImage, product]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showShareMenu && !e.target.closest('.share-menu')) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showShareMenu]);

  // Function to format description text
  const formatDescription = (text) => {
    if (!text) return '';
    
    const plainText = text.replace(/<[^>]*>/g, '');
    const lines = plainText.split('\n').filter(line => line.trim());
    
    return lines.map((line, index) => {
      if (line.includes('•') || line.includes('-') || line.includes('*')) {
        return (
          <li key={index} className="mb-1 flex items-start gap-2">
            <span className="text-red-600">•</span>
            <span>{line.replace(/[•\-*]/g, '').trim()}</span>
          </li>
        );
      }
      else if (line.toUpperCase() === line && line.length < 50 && line.trim().length > 0) {
        return (
          <h3 key={index} className="font-bold text-gray-800 mt-3 mb-1">
            {line.trim()}
          </h3>
        );
      }
      else {
        return (
          <p key={index} className="mb-2">
            {line.trim()}
          </p>
        );
      }
    });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error} />;
  if (!product) return <ErrorAlert message="រកមិនឃើញទំនិញ" />;

  const discountedPrice = product.price * (1 - product.discount_percent / 100);
  const images = product.images || [];
  const mainImageUrl = images[selectedImage] 
    ? getImageUrl(images[selectedImage].filepath, 'products')
    : 'https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image';

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Back Button and Share */}
      <div className="flex items-center justify-between mb-6">
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600"
        >
          <FiArrowLeft size={18} />
          <span>ត្រឡប់ក្រោយ</span>
        </Link>

        {/* Share Button */}
        <div className="relative">
          <button
            onClick={handleNativeShare}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors rounded"
          >
            {copied ? <FiCheck size={18} /> : <FiShare2 size={18} />}
            <span className="text-sm hidden sm:inline">ចែករំលែក</span>
          </button>

          {/* Share Menu Dropdown */}
          {showShareMenu && !navigator.share && (
            <div className="share-menu absolute right-0 top-full mt-2 bg-white border border-gray-200 shadow-lg rounded z-50 min-w-[200px]">
              <div className="py-2">
                <button
                  onClick={shareToFacebook}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                >
                  <FaFacebook className="text-blue-600" size={18} />
                  <span>Facebook</span>
                </button>
                <button
                  onClick={shareToTelegram}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                >
                  <FaTelegramPlane className="text-blue-500" size={18} />
                  <span>Telegram</span>
                </button>
                <button
                  onClick={shareToTwitter}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                >
                  <FaTwitter className="text-blue-400" size={18} />
                  <span>Twitter</span>
                </button>
                <button
                  onClick={shareToWhatsApp}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                >
                  <FaWhatsapp className="text-green-500" size={18} />
                  <span>WhatsApp</span>
                </button>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={copyLinkToClipboard}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                >
                  <FaLink className="text-gray-500" size={18} />
                  <span>ចម្លងតំណ</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rest of your product detail UI remains the same */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* Image Gallery */}
        <div>
          {/* Main Image with Click to Zoom */}
          <div 
            className="bg-gray-100 border border-gray-200 mb-3 overflow-hidden relative group cursor-pointer"
            onClick={() => openModal(mainImageUrl)}
          >
            <img
              src={mainImageUrl}
              alt={product.title}
              className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image";
              }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/90 p-2 rounded-full">
                <FiZoomIn size={24} className="text-gray-700" />
              </div>
            </div>
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`border-2 overflow-hidden cursor-pointer transition-all ${
                    selectedImage === index ? 'border-red-600' : 'border-transparent hover:border-red-300'
                  }`}
                >
                  <img
                    src={getImageUrl(image.filepath, 'products')}
                    alt={`thumb-${index}`}
                    className="w-full h-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/100x100/F3F4F6/9CA3AF?text=Error";
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          {/* Category */}
          <Link 
            to={`/products?category=${product.category?.id}`} 
            className="inline-block text-xs text-red-600 bg-red-50 px-3 py-1 mb-3 hover:bg-red-600 hover:text-white"
          >
            {product.category?.name || 'ផលិតផល'}
          </Link>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {product.title}
          </h1>

          {/* Price */}
          <div className="bg-red-50 border border-red-200 p-4 mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-bold text-red-600">
                ${(discountedPrice || product.price).toFixed(2)}
              </span>
              
              {product.discount_percent > 0 && (
                <>
                  <span className="text-gray-500 line-through">
                    ${product.price.toFixed(2)}
                  </span>
                  <span className="bg-red-600 text-white text-xs font-bold px-2 py-1">
                    -{product.discount_percent}%
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h2 className="font-bold text-gray-800 mb-3">ព័ត៌មានលម្អិត</h2>
            <div className="text-gray-700 text-sm leading-relaxed bg-gray-50 p-4 border border-gray-200">
              {product.description ? (
                <div className="description-content">
                  {formatDescription(product.description)}
                </div>
              ) : (
                <p className="text-gray-500">គ្មានព័ត៌មានលម្អិត</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleTelegramContact}
              className="w-full bg-[#0088cc] text-white py-3 font-medium flex items-center justify-center gap-2 hover:bg-[#0077b5] transition-colors"
            >
              <FaTelegramPlane size={18} />
              <span>ទំនាក់ទំនង Telegram</span>
            </button>

            <button
              onClick={handlePhoneContact}
              className="w-full bg-gray-100 border border-gray-300 text-gray-700 py-3 font-medium flex items-center justify-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
            >
              <FiMessageCircle size={18} />
              <span>ទូរស័ព្ទមកយើង</span>
            </button>
          </div>

          {/* Telegram Info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="flex items-center gap-2">
              <FaTelegramPlane className="text-[#0088cc]" size={16} />
              <span className="text-sm text-blue-800">ទំនាក់ទំនងតាម Telegram: @{TELEGRAM_USERNAME}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Products */}
      {!loadingLatest && latestProducts.length > 0 && (
        <div className="border-t border-gray-200 pt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">ផលិតផលថ្មីៗ</h2>
            <Link to="/products?sort=latest" className="text-red-600 text-sm flex items-center gap-1 hover:text-red-700">
              មើលទាំងអស់
              <FiArrowLeft className="rotate-180" size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {latestProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}

      {/* Full Screen Image Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black z-[100] flex items-center justify-center"
          onClick={closeModal}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 z-20 text-white hover:text-red-500 transition-colors p-2"
          >
            <FiX size={32} />
          </button>

          {/* Previous Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPreviousImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 text-white hover:text-red-500 transition-colors p-3 bg-black/50 hover:bg-black/70"
            >
              <FiChevronLeft size={40} />
            </button>
          )}

          {/* Next Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 text-white hover:text-red-500 transition-colors p-3 bg-black/50 hover:bg-black/70"
            >
              <FiChevronRight size={40} />
            </button>
          )}

          {/* Main Image */}
          <div 
            className="w-full h-full flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={modalImage}
              alt="Product full view"
              className="max-w-full max-h-full object-contain animate-scaleIn"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image";
              }}
            />
          </div>

          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-black/70 text-white px-3 py-1 text-sm rounded">
              {selectedImage + 1} / {images.length}
            </div>
          )}

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2 px-4 pb-4 overflow-x-auto">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(index);
                    setModalImage(getImageUrl(image.filepath, 'products'));
                  }}
                  className={`w-16 h-16 border-2 overflow-hidden transition-all flex-shrink-0 ${
                    selectedImage === index 
                      ? 'border-red-600 scale-105' 
                      : 'border-white/50 hover:border-red-400'
                  }`}
                >
                  <img
                    src={getImageUrl(image.filepath, 'products')}
                    alt={`thumb-${index}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/64x64/F3F4F6/9CA3AF?text=Error";
                    }}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Instruction Hint */}
          <div className="absolute bottom-4 right-4 z-20 text-white/50 text-xs hidden md:block">
            ← → ប្រើក្តារចុចដើម្បីប្តូររូបភាព
          </div>
        </div>
      )}

      {/* Add custom styles for description and animations */}
      <style>{`
        .description-content {
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .description-content p {
          margin-bottom: 0.75rem;
        }
        .description-content li {
          margin-bottom: 0.5rem;
        }
        .description-content h3 {
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
